import React, { useState, useEffect } from "react"
import {
    Box,
    Container,
    Typography,
    Button,
    Snackbar,
    Alert,
} from "@mui/material"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import { useTranslation } from "react-i18next"
import { useParams, useNavigate } from "react-router-dom"
import apiClient from "../../services/apiClient"
import * as adminService from "../../services/adminService"
import CustomerForm from "../../components/customers/CustomerForm"

const CustomerEditPage: React.FC = () => {
    const { t } = useTranslation()
    const { customerId } = useParams<{ customerId: string }>()
    const navigate = useNavigate()
    const [companyId, setCompanyId] = useState("")
    const [customer, setCustomer] = useState<any>(null)
    const [loading, setLoading] = useState(true)
    const [snackbar, setSnackbar] = useState<{
        open: boolean
        message: string
        severity: "success" | "error"
    }>({
        open: false,
        message: "",
        severity: "success",
    })

    useEffect(() => {
        let cancelled = false
        ;(async () => {
            try {
                const res = await apiClient.get("/api/companies")
                if (cancelled) return
                const list = res.companies || []
                if (list.length > 0) setCompanyId(list[0].id)
            } catch (e: any) {
                console.error("Failed to load companies:", e)
            }
        })()
        return () => {
            cancelled = true
        }
    }, [])

    useEffect(() => {
        if (!companyId || !customerId) return
        let cancelled = false
        ;(async () => {
            setLoading(true)
            try {
                const c = await adminService.getCustomer(companyId, customerId)
                if (cancelled) return
                setCustomer(c)
            } catch (e: any) {
                console.error("Failed to load customer:", e)
            } finally {
                if (!cancelled) setLoading(false)
            }
        })()
        return () => {
            cancelled = true
        }
    }, [companyId, customerId])

    const handleSubmit = async (data: any) => {
        if (!companyId || !customerId) return
        try {
            await adminService.updateCustomer(companyId, customerId, data)
            navigate(`/admin/customers/${customerId}`)
        } catch (error: any) {
            const msg =
                error?.response?.data?.message ||
                error?.message ||
                String(t("customersPages.edit.errors.updateFailed"))
            setSnackbar({ open: true, message: msg, severity: "error" })
            throw error
        }
    }

    if (loading) {
        return (
            <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
                <Container maxWidth="lg">
                    <Typography>
                        {t("customersPages.common.loading")}
                    </Typography>
                </Container>
            </Box>
        )
    }

    if (!customer) {
        return (
            <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
                <Container maxWidth="lg">
                    <Typography>
                        {t("customersPages.common.notFound")}
                    </Typography>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate("/admin/customers")}
                        sx={{ mt: 2 }}
                    >
                        {t("customersPages.common.backToCustomers")}
                    </Button>
                </Container>
            </Box>
        )
    }

    return (
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
            <Container maxWidth="lg">
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mb: 3,
                    }}
                >
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() =>
                            navigate(`/admin/customers/${customerId}`)
                        }
                    >
                        {t("customersPages.common.back")}
                    </Button>
                    <Typography variant="h4">
                        {t("customersPages.edit.titlePrefix")}{" "}
                        {customer.type === "business"
                            ? customer.company_name
                            : customer.name}
                    </Typography>
                </Box>

                <CustomerForm
                    companyId={companyId}
                    initialData={customer}
                    onSubmit={handleSubmit}
                    onCancel={() => navigate(`/admin/customers/${customerId}`)}
                    submitLabel={String(t("customersPages.edit.submit"))}
                />
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={4000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                >
                    <Alert
                        onClose={() =>
                            setSnackbar({ ...snackbar, open: false })
                        }
                        severity={snackbar.severity}
                        variant="filled"
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Container>
        </Box>
    )
}

export default CustomerEditPage
